import './styles/default.scss';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useStore } from '@/store/useStore';
import useEventManagerEmitter from '@/event-manager/use-event-manager-emiter';
import { GAME_WINDOW_TO_CLIENT_EVENTS } from '@/event-manager/types';
import Lottie, {
  type LottieRef,
  type LottieRefCurrentProps,
} from 'lottie-react';
import { isEqual } from 'lodash';
import { getBalanceData } from '@/unified-modules/wallet/helpers/get-balance-data';
import { POPUP_TYPES } from '@/features/Popup/constants';
import { removeFromQueueSpecific } from '@/store/actions';
import { ModalPresenter } from '@fanduel/cpe-um-bundle/core-modal-presenter/library';
import {
  trackWinPageCTAClicked,
  trackWinPageViewed,
} from '../helpers/swjTracking';

const SHORTEN_ANIMATION_SECONDS = 3000;

export enum WinTierEnum {
  MINI = 'MINI',
  MINOR = 'MINOR',
  MEGA = 'MEGA',
  MAJOR = 'MAJOR',
}

const SpinningAnimationsBackgroudDurationRecord: Record<WinTierEnum, number> = {
  [WinTierEnum.MINI]: 6500,
  [WinTierEnum.MINOR]: 6500,
  [WinTierEnum.MEGA]: 6500,
  [WinTierEnum.MAJOR]: 5500,
};

export interface WonJackpotData {
  name: WinTierEnum;
  shortName?: string;
  amount: number;
  win_particles_animation: object | undefined;
  win_spin_animation: object | undefined;
}

interface JackpotWinningAnimationProps {
  wonJackpot: WonJackpotData;
}

const USDollar = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const getNomarlizedTierName = (name = '') => {
  const winTier = name?.toUpperCase();
  let normalizedName: WinTierEnum | undefined;
  for (const tier of Object.values(WinTierEnum)) {
    if (winTier.includes(tier)) {
      normalizedName = WinTierEnum[tier as keyof typeof WinTierEnum];
    }
  }

  return normalizedName;
};

const fetchAnimation = async (
  url: string | undefined,
): Promise<object | null> => {
  if (!url) {
    return null;
  }
  try {
    const response = await fetch(url);
    if (response.ok) {
      return await response.json();
    }
  } catch (error) {
    console.error('Error fetching animation:', error);
  }
  return null;
};

const isValidLottieAnimation = (data: unknown): data is object =>
  data != null &&
  typeof data === 'object' &&
  Array.isArray((data as { layers?: unknown }).layers);

type LottieAnimationRecord = Partial<Record<WinTierEnum, object | null>>;

export const JackpotWinningAnimation = ({
  wonJackpot,
}: JackpotWinningAnimationProps) => {
  const [backgroundAnimationsRecord, setBackgroundAnimationsRecord] = useState<
    LottieAnimationRecord | undefined
  >(undefined);
  const [spinningAnimationsRecord, setSpinningAnimationsRecord] = useState<
    LottieAnimationRecord | undefined
  >(undefined);
  const [wheelIdleRecord, setWheelIdleRecord] = useState<
    object | null | undefined
  >(undefined);
  const [wonJackpotData, setWonJackpotData] = useState<
    WonJackpotData | undefined
  >(undefined);
  const [isSpinning, setIsSpinning] = useState(false);
  const [showCongrats, setShowCongrats] = useState(false);
  const [showBackgroundAnimation, setShowBackgroundAnimation] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);
  const lottieRef = useRef<LottieRefCurrentProps | null>(null);
  const prevWonJackpotData = useRef<WonJackpotData | null>(null);
  const { provider: gameProvider } = useStore((state) => state?.gameContext);
  const miniBackgroundLottieRef = useRef<LottieRefCurrentProps | null>(null);
  const minorBackgroundLottieRef = useRef<LottieRefCurrentProps | null>(null);
  const majorBackgroundLottieRef = useRef<LottieRefCurrentProps | null>(null);
  const megaBackgroundLottieRef = useRef<LottieRefCurrentProps | null>(null);
  const backgroundLottieRefs: Record<
    WinTierEnum,
    typeof miniBackgroundLottieRef
  > = {
    [WinTierEnum.MINI]: miniBackgroundLottieRef,
    [WinTierEnum.MINOR]: minorBackgroundLottieRef,
    [WinTierEnum.MAJOR]: majorBackgroundLottieRef,
    [WinTierEnum.MEGA]: megaBackgroundLottieRef,
  };
  const [playingBackgroundAnimation, setPlayingBackgroundAnimation] =
    useState<WinTierEnum | null>(null);
  const jackpotAudioRef = useRef<HTMLAudioElement | null>(null);
  const { emitClientEvent } = useEventManagerEmitter();

  const reloadBalance = useCallback(
    (balance) =>
      emitClientEvent(GAME_WINDOW_TO_CLIENT_EVENTS.RELOAD_BALANCE, balance),
    [emitClientEvent],
  );
  const autoSpinStop = useCallback(
    () => emitClientEvent(GAME_WINDOW_TO_CLIENT_EVENTS.AUTO_SPIN_STOP),
    [emitClientEvent],
  );
  const autoSpinResume = useCallback(
    () => emitClientEvent(GAME_WINDOW_TO_CLIENT_EVENTS.AUTO_SPIN_RESUME),
    [emitClientEvent],
  );

  const { spinInProgress: isSpinInProgress } = useStore(
    (state) => state?.gameWindowContext,
  );
  const { jackpotData, jackpotOptInStatus: optedIn } = useStore(
    (state) => state.swj,
  );
  const swjWinningSound = jackpotData?.win_animation_sound?.url;
  const swjWheelIdleUrl = jackpotData?.win_idle_animation?.url;
  const winLogoImgUrl = jackpotData?.alternate_logo?.url;
  const swjTiers = jackpotData?.tiers;
  const optedInOption = Number(optedIn) > 0 ? 'in' : 'out';

  const handleAnimationComplete = () => {
    setTimeout(() => {
      setShowCongrats(true);
      //waiting time until resetting the wheel, the animation still plays even if text appears
      setTimeout(() => {
        setIsSpinning(false);
      }, SHORTEN_ANIMATION_SECONDS);
    }, 300);
  };

  const exitJackpot = () => {
    const context = {
      jackpotTierName: wonJackpot?.name,
      amount: wonJackpot?.amount,
      jackpotTierId: wonJackpot?.['id'],
    };
    const linkText = 'back_to_game';
    trackWinPageCTAClicked(linkText, optedInOption, wonJackpotData, context);

    const SET_BALANCE_GAME_PROVIDERS = ['nyx', 'lnw', 'aristocrat', 'fusion'];
    if (
      gameProvider &&
      SET_BALANCE_GAME_PROVIDERS.includes(gameProvider.toLowerCase())
    ) {
      getBalanceData((balance) => {
        reloadBalance(balance);
      });
    } else {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      reloadBalance();
    }

    setWonJackpotData(undefined);
    removeFromQueueSpecific({ type: POPUP_TYPES.JACKPOT_WIN });
    ModalPresenter.dismissAllModals();
    document.body.removeChild(
      document.body.getElementsByClassName('jackpot-win-style-activator')[0],
    );
    autoSpinResume();
  };

  useEffect(() => {
    if (wonJackpot && !isSpinInProgress) {
      if (!wonJackpot.name) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- sync won jackpot view from incoming payload
        setWonJackpotData(undefined);
      }

      const normalizedName: WinTierEnum | undefined = getNomarlizedTierName(
        wonJackpot?.shortName || wonJackpot.name,
      );
      if (!normalizedName) {
        setWonJackpotData(undefined);
      }
      autoSpinStop();
      setWonJackpotData({
        name: normalizedName as WinTierEnum,
        amount: wonJackpot.amount,
        win_particles_animation: wonJackpot.win_particles_animation,
        win_spin_animation: wonJackpot.win_spin_animation,
      });
    }
  }, [wonJackpot, isSpinInProgress]);

  useEffect(() => {
    jackpotAudioRef.current = new Audio(swjWinningSound);
    jackpotAudioRef.current.load();
  }, []);

  useEffect(() => {
    if (jackpotAudioRef.current) {
      if (isSpinning) {
        const durationInSeconds =
          (lottieRef as LottieRef)?.current?.getDuration() ?? 0;

        (lottieRef as LottieRef)?.current?.goToAndPlay(0, true);
        jackpotAudioRef.current.play();

        setTimeout(
          () => {
            handleAnimationComplete();
          },
          durationInSeconds * 1000 - SHORTEN_ANIMATION_SECONDS,
        );
        return;
      }
      (lottieRef as LottieRef)?.current?.stop();
    }
  }, [isSpinning]);

  useEffect(() => {
    if (showBackgroundAnimation && wonJackpotData?.name) {
      if (
        showBackgroundAnimation &&
        backgroundAnimationsRecord?.[wonJackpotData.name]
      ) {
        backgroundLottieRefs[wonJackpotData.name]?.current?.setSubframe(false);
        backgroundLottieRefs[wonJackpotData.name]?.current?.goToAndStop(
          0,
          true,
        );
        backgroundLottieRefs[wonJackpotData.name]?.current?.play();
        // eslint-disable-next-line react-hooks/set-state-in-effect -- mirror background lottie playback in UI state
        setPlayingBackgroundAnimation(wonJackpotData.name);
      } else {
        backgroundLottieRefs[wonJackpotData.name]?.current?.stop();
        setPlayingBackgroundAnimation(null);
      }
    }
  }, [
    backgroundAnimationsRecord,
    showBackgroundAnimation,
    wonJackpotData?.name,
  ]);

  useEffect(() => {
    if (
      wonJackpotData &&
      !isEqual(wonJackpotData, prevWonJackpotData.current)
    ) {
      prevWonJackpotData.current = wonJackpotData;
      const context = {
        jackpotTierName: wonJackpot?.name,
        amount: wonJackpot?.amount,
        jackpotTierId: wonJackpot?.['id'],
      };
      trackWinPageViewed(context, optedInOption, wonJackpotData);
    }
  }, [wonJackpotData]);

  useEffect(() => {
    const loadAnimations = async () => {
      const backgroundAnimations: Partial<Record<WinTierEnum, object | null>> =
        {};
      const spinningAnimations: Partial<Record<WinTierEnum, object | null>> =
        {};

      setWheelIdleRecord(await fetchAnimation(swjWheelIdleUrl));

      for (const tier of swjTiers ?? []) {
        const normalizedName = getNomarlizedTierName(
          tier?.short_tier_name?.[0]?.text ?? tier?.tier_name?.[0]?.text,
        );

        if (
          !normalizedName ||
          !Object.values(WinTierEnum).includes(normalizedName)
        ) {
          continue;
        }

        const winParticlesUrl = tier.win_particles_animation?.url;
        const winSpinUrl = tier.win_spin_animation?.url;

        backgroundAnimations[normalizedName] =
          await fetchAnimation(winParticlesUrl);
        spinningAnimations[normalizedName] = await fetchAnimation(winSpinUrl);
      }

      setBackgroundAnimationsRecord(backgroundAnimations);
      setSpinningAnimationsRecord(spinningAnimations);
    };

    loadAnimations();
  }, [swjTiers, swjWheelIdleUrl]);

  useEffect(() => {
    if (lottieRef.current && spinningAnimationsRecord) {
      lottieRef.current.goToAndStop(0, true);
    }
  }, [spinningAnimationsRecord]);

  const handleSpinClick = () => {
    setShowOverlay(false);
    setTimeout(() => {
      setIsSpinning(true);
    }, 300);

    setTimeout(
      () => {
        setShowBackgroundAnimation(true);
      },
      SpinningAnimationsBackgroudDurationRecord[wonJackpotData?.name ?? ''],
    );

    const linkText = 'spin';
    trackWinPageCTAClicked(linkText, optedInOption, wonJackpotData);
  };

  const wonTierSpinAnimation =
    spinningAnimationsRecord?.[wonJackpotData?.name as WinTierEnum];

  if (
    isSpinInProgress ||
    !wonJackpotData ||
    !isValidLottieAnimation(wheelIdleRecord) ||
    !isValidLottieAnimation(wonTierSpinAnimation)
  ) {
    return null;
  }

  return (
    <div className="jackpot-animations-container">
      <div className="jackpot-logo">
        <img
          src={winLogoImgUrl}
          data-testid="jackpot-logo"
          alt="win-screen-jackpot-logo"
          className="jackpot-logo-alternate-img"
        />
      </div>

      <div
        className={`idle-state ${!isSpinning && !showCongrats ? 'show' : ''}`}
        data-testid="idle-state"
      >
        <Lottie
          className="iddle-animation-wrapper"
          animationData={wheelIdleRecord}
          loop={true}
        />
        <div
          className={`iddle-overlay ${showOverlay ? 'slide-in' : 'slide-out'}`}
        >
          <p data-testid="jackpot-won-intro-message">
            Spin the wheel to reveal <br /> your jackpot win!
          </p>
          <div className="pulse-button-wrapper">
            <button
              data-testid="pulse-button"
              className="pulse-button"
              onClick={handleSpinClick}
            />
          </div>
        </div>
      </div>

      <div
        className={`spinning-state ${isSpinning || showCongrats ? 'show' : ''}`}
        data-testid="spinning-state"
      >
        <div className="background-animation">
          {isValidLottieAnimation(
            backgroundAnimationsRecord?.[WinTierEnum.MINI],
          ) && (
            <Lottie
              className={`bg-animation-wrapper mini ${playingBackgroundAnimation === WinTierEnum.MINI ? 'playing' : ''}`}
              animationData={backgroundAnimationsRecord[WinTierEnum.MINI]}
              loop={true}
              autoPlay={false}
              lottieRef={miniBackgroundLottieRef}
              data-testid="lottie-mini"
            />
          )}
          {wonJackpotData.name === WinTierEnum.MINOR &&
            isValidLottieAnimation(
              backgroundAnimationsRecord?.[WinTierEnum.MINOR],
            ) && (
              <Lottie
                className={`bg-animation-wrapper minor ${playingBackgroundAnimation === WinTierEnum.MINOR ? 'playing' : ''}`}
                animationData={backgroundAnimationsRecord[WinTierEnum.MINOR]}
                loop={true}
                autoPlay={false}
                lottieRef={minorBackgroundLottieRef}
                data-testid="lottie-minor"
              />
            )}
          {wonJackpotData.name === WinTierEnum.MAJOR &&
            isValidLottieAnimation(
              backgroundAnimationsRecord?.[WinTierEnum.MAJOR],
            ) && (
              <Lottie
                className={`bg-animation-wrapper major ${playingBackgroundAnimation === WinTierEnum.MAJOR ? 'playing' : ''}`}
                animationData={backgroundAnimationsRecord[WinTierEnum.MAJOR]}
                loop={true}
                autoPlay={false}
                lottieRef={majorBackgroundLottieRef}
                data-testid="lottie-major"
              />
            )}
          {wonJackpotData.name === WinTierEnum.MEGA &&
            isValidLottieAnimation(
              backgroundAnimationsRecord?.[WinTierEnum.MEGA],
            ) && (
              <Lottie
                className={`bg-animation-wrapper mega ${playingBackgroundAnimation === WinTierEnum.MEGA ? 'playing' : ''}`}
                animationData={backgroundAnimationsRecord[WinTierEnum.MEGA]}
                loop={true}
                autoPlay={false}
                lottieRef={megaBackgroundLottieRef}
                data-testid="lottie-mega"
              />
            )}
        </div>
        <Lottie
          className="spin-animation-wrapper"
          animationData={wonTierSpinAnimation}
          loop={false}
          autoPlay={false}
          lottieRef={lottieRef}
        />

        {showCongrats && (
          <div className={'congrats-overlay slide-in'}>
            <div className="text-wrapper">
              <div>You won a</div>
              <div data-testid="jackpot-type-won-message">
                {wonJackpotData.name.toUpperCase()} JACKPOT
              </div>
              <div data-testid="jackpot-value-won-message">
                {USDollar.format(wonJackpotData.amount)}
              </div>
              <div data-testid="jackpot-won-message">
                Winnings are now <br /> in your account!
              </div>
              <div>*Subject to federal and state withholding requirements</div>
            </div>
            <div className="button-wrapper">
              <button data-testid="back-to-game-button" onClick={exitJackpot}>
                Back to game
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
